import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API_BASE_URL from '../../config';
import {
    Calendar,
    MapPin,
    Clock,
    CheckCircle2,
    XCircle,
    HelpCircle,
    Utensils,
    Home,
    Plane,
    MessageSquare,
    ExternalLink,
    Download,
    Edit3,
    Heart,
    Sparkles,
} from 'lucide-react';
import { SUPPORTED_LANGUAGES, getRsvpTranslation } from './inviteLanguages';
import { Globe } from 'lucide-react';
import './InviteRsvpPage.css';

interface EventDetails {
    id?: string;
    eventName: string;
    eventType: string;
    customEventType?: string;
    eventDate: string;
    eventVenue: string;
    eventCity: string;
    mediaUrl?: string;
    mediaType?: 'image' | 'video' | 'none';
    messageTemplate?: string;
    hostName?: string;
    hostPhone?: string;
    collectDietary?: boolean;
    collectAccommodation?: boolean;
}

interface RecipientDetails {
    id?: string;
    name: string;
    phone?: string;
    email?: string;
    customFields?: {
        salutation?: string;
        relationship?: string;
        familySide?: string;
    };
    rsvpStatus: 'pending' | 'accepted' | 'declined' | 'maybe';
    attendeesCount?: number;
    adultsCount?: number;
    kidsCount?: number;
    dietaryPreference?: 'none' | 'vegetarian' | 'non-vegetarian' | 'jain' | 'vegan' | 'eggetarian' | 'other';
    needsAccommodation?: boolean;
    arrivalDate?: string;
    transportDetails?: string;
    rsvpNote?: string;
    rsvpRespondedAt?: string;
}

export const InviteRsvpPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();

    const [event, setEvent] = useState<EventDetails | null>(null);
    const [recipient, setRecipient] = useState<RecipientDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lang, setLang] = useState<string>('en');
    const t = getRsvpTranslation(lang);

    // Form state
    const [rsvpStatus, setRsvpStatus] = useState<'accepted' | 'declined' | 'maybe'>('accepted');
    const [adultsCount, setAdultsCount] = useState<number>(1);
    const [kidsCount, setKidsCount] = useState<number>(0);
    const [dietaryPreference, setDietaryPreference] = useState<'none' | 'vegetarian' | 'non-vegetarian' | 'jain' | 'vegan' | 'eggetarian' | 'other'>('none');
    const [needsAccommodation, setNeedsAccommodation] = useState<boolean>(false);
    const [arrivalDate, setArrivalDate] = useState<string>('');
    const [transportDetails, setTransportDetails] = useState<string>('');
    const [rsvpNote, setRsvpNote] = useState<string>('');
    
    // View state (editing vs submitted/view-mode)
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Countdown Timer
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, mins: 0 });
    const [isWakingServer, setIsWakingServer] = useState(false);

    const cleanToken = (token || '').trim();

    const fetchRsvpDetails = async (isManualRetry = false) => {
        if (!cleanToken) {
            setError('Invalid RSVP link: no invitation token provided.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        const wakeTimer = setTimeout(() => setIsWakingServer(true), 2500);

        let attempts = 0;
        const maxAttempts = 3;

        // Try primary API_BASE_URL first, then fallback to same-origin /api if different
        const endpoints = [
            `${API_BASE_URL}/api/invite/rsvp/${cleanToken}`,
            `/api/invite/rsvp/${cleanToken}`,
        ];

        while (attempts < maxAttempts) {
            attempts++;
            const endpoint = endpoints[(attempts - 1) % endpoints.length];

            try {
                const response = await fetch(endpoint);
                clearTimeout(wakeTimer);
                setIsWakingServer(false);

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('This RSVP invitation link is invalid or has expired.');
                    }
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.message || 'Failed to load invitation details.');
                }

                const data = await response.json();
                setEvent(data.event);
                    if (data.event.language) {
                        setLang(data.event.language);
                    }
                setRecipient(data.recipient);

                if (data.recipient.rsvpStatus && data.recipient.rsvpStatus !== 'pending') {
                    setRsvpStatus(data.recipient.rsvpStatus);
                    setIsSubmitted(true);
                }

                setAdultsCount(data.recipient.adultsCount || 1);
                setKidsCount(data.recipient.kidsCount || 0);
                setDietaryPreference(data.recipient.dietaryPreference || 'none');
                setNeedsAccommodation(Boolean(data.recipient.needsAccommodation));
                setArrivalDate(data.recipient.arrivalDate || '');
                setTransportDetails(data.recipient.transportDetails || '');
                setRsvpNote(data.recipient.rsvpNote || '');
                setLoading(false);
                return;
            } catch (err: any) {
                // If it's a 404 invalid token, don't retry in a loop
                if (err.message && err.message.includes('invalid or has expired')) {
                    clearTimeout(wakeTimer);
                    setIsWakingServer(false);
                    setError(err.message);
                    setLoading(false);
                    return;
                }

                if (attempts < maxAttempts) {
                    await new Promise(r => setTimeout(r, 2000));
                    continue;
                }

                clearTimeout(wakeTimer);
                setIsWakingServer(false);
                setError(err.message && !err.message.includes('fetch') ? err.message : 'Could not connect to the event server. Please tap retry.');
                setLoading(false);
                break;
            }
        }
    };

    useEffect(() => {
        if (cleanToken) {
            fetchRsvpDetails();
        }
    }, [cleanToken]);

    // Live Countdown
    useEffect(() => {
        if (!event?.eventDate) return;

        const updateTimer = () => {
            const difference = +new Date(event.eventDate) - +new Date();
            if (difference > 0) {
                setCountdown({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    mins: Math.floor((difference / 1000 / 60) % 60),
                });
            } else {
                setCountdown({ days: 0, hours: 0, mins: 0 });
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 60000);
        return () => clearInterval(interval);
    }, [event?.eventDate]);

    // Submit RSVP Handler
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const payload = {
                rsvpStatus,
                adultsCount: rsvpStatus === 'accepted' ? adultsCount : 0,
                kidsCount: rsvpStatus === 'accepted' ? kidsCount : 0,
                attendeesCount: rsvpStatus === 'accepted' ? (adultsCount + kidsCount) : (rsvpStatus === 'maybe' ? 1 : 0),
                dietaryPreference: rsvpStatus === 'accepted' ? dietaryPreference : 'none',
                needsAccommodation: rsvpStatus === 'accepted' ? needsAccommodation : false,
                arrivalDate: rsvpStatus === 'accepted' && needsAccommodation ? arrivalDate : undefined,
                transportDetails: rsvpStatus === 'accepted' ? transportDetails : undefined,
                rsvpNote,
            };

            let response: Response;
            try {
                response = await fetch(`${API_BASE_URL}/api/invite/rsvp/${cleanToken}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
            } catch (networkErr) {
                // Fallback to same-origin proxy
                response = await fetch(`/api/invite/rsvp/${cleanToken}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to submit your RSVP.');
            }

            setRecipient(prev => prev ? { ...prev, ...payload, rsvpRespondedAt: new Date().toISOString() } : null);
            setIsSubmitted(true);
            setIsEditing(false);
        } catch (err: any) {
            alert('Error submitting RSVP: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Helper: Google Calendar URL
    const getGoogleCalendarUrl = () => {
        if (!event) return '#';
        const start = new Date(event.eventDate).toISOString().replace(/-|:|\.\d\d\d/g, '');
        const endDate = new Date(new Date(event.eventDate).getTime() + 4 * 60 * 60 * 1000); // 4 hrs default
        const end = endDate.toISOString().replace(/-|:|\.\d\d\d/g, '');
        
        const details = `You are cordially invited to ${event.eventName}! Venue: ${event.eventVenue}, ${event.eventCity}.`;
        const location = `${event.eventVenue}, ${event.eventCity}`;

        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.eventName)}&dates=${start}/${end}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
    };

    // Helper: Download .ICS calendar file
    const downloadIcsFile = () => {
        if (!event) return;
        const start = new Date(event.eventDate).toISOString().replace(/-|:|\.\d\d\d/g, '');
        const endDate = new Date(new Date(event.eventDate).getTime() + 4 * 60 * 60 * 1000);
        const end = endDate.toISOString().replace(/-|:|\.\d\d\d/g, '');

        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Shadigram E-Invitations//RSVP Event//EN',
            'BEGIN:VEVENT',
            `SUMMARY:${event.eventName}`,
            `DESCRIPTION:You are cordially invited to ${event.eventName}. Hosted by ${event.hostName || 'the family'}.`,
            `LOCATION:${event.eventVenue}, ${event.eventCity}`,
            `DTSTART:${start}`,
            `DTEND:${end}`,
            'STATUS:CONFIRMED',
            'END:VEVENT',
            'END:VCALENDAR',
        ].join('\r\n');

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `${event.eventName.replace(/[^a-z0-9]/gi, '_')}.ics`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Helper: Google Maps URL
    const getGoogleMapsUrl = () => {
        if (!event) return '#';
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${event.eventVenue}, ${event.eventCity}`)}`;
    };

    // Emoji Helper for Event Types
    const getEventEmoji = (type?: string) => {
        switch (type) {
            case 'marriage': return '💒';
            case 'birthday': return '🎂';
            case 'housewarming': return '🏠';
            case 'anniversary': return '💍';
            case 'corporate': return '🏢';
            case 'engagement': return '💎';
            case 'baby-shower': return '👶';
            default: return '✨';
        }
    };

    if (loading && !event) {
        return (
            <div className="rsvp-page-wrapper">
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <div style={{ width: 44, height: 44, border: '3px solid rgba(139,92,246,0.3)', borderTopColor: '#C084FC', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1.25rem' }} />
                    <p style={{ color: '#F8FAFC', fontWeight: 700, fontSize: '1rem', margin: '0 0 0.5rem' }}>
                        {isWakingServer ? 'Connecting to Event Server...' : 'Loading Invitation Details...'}
                    </p>
                    {isWakingServer && (
                        <p style={{ color: '#94A3B8', fontSize: '0.85rem', margin: 0, maxWidth: 320 }}>
                            ⚡ Waking up the secure server for you... This takes just a moment.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rsvp-page-wrapper">
                <div className="rsvp-container" style={{ textAlign: 'center', padding: '3.5rem 2rem' }}>
                    <span style={{ fontSize: '3.5rem', display: 'block', marginBottom: '1rem' }}>⚠️</span>
                    <h2 style={{ color: '#F87171', margin: '0 0 0.75rem', fontSize: '1.4rem', fontWeight: 800 }}>Invitation Unavailable</h2>
                    <p style={{ color: '#CBD5E1', margin: '0 0 1.75rem', fontSize: '0.92rem', lineHeight: 1.5, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
                        {error}
                    </p>
                    <button
                        onClick={() => fetchRsvpDetails(true)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '11px 26px',
                            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                            color: '#FFF',
                            borderRadius: '12px',
                            border: 'none',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
                        }}
                    >
                        <span>🔄 Try Again / Retry</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="rsvp-page-wrapper">
            <div className="rsvp-container">

                {/* 🌐 Multi-Language Switcher Toolbar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    marginBottom: '1rem',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    background: 'rgba(30, 41, 59, 0.6)',
                    border: '1px solid rgba(139, 92, 246, 0.25)',
                    backdropFilter: 'blur(8px)',
                }}>
                    <Globe size={15} color="#A78BFA" />
                    <span style={{ fontSize: '0.8rem', color: '#CBD5E1', fontWeight: 600 }}>{t.switchLanguage}:</span>
                    <select
                        value={lang}
                        onChange={(e) => setLang(e.target.value)}
                        style={{
                            padding: '4px 10px',
                            borderRadius: '8px',
                            border: '1px solid rgba(139, 92, 246, 0.4)',
                            background: 'rgba(15, 23, 42, 0.85)',
                            color: '#F8FAFC',
                            fontSize: '0.82rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            outline: 'none'
                        }}
                    >
                        {SUPPORTED_LANGUAGES.map(l => (
                            <option key={l.code} value={l.code}>
                                {l.flag} {l.nativeName} ({l.label})
                            </option>
                        ))}
                    </select>
                </div>

                {/* 1. Event Hero Header */}
                <div className="rsvp-header-banner">
                    <div className="rsvp-top-ornament">✦ ✦ ✦</div>
                    <div className="rsvp-event-badge">
                        <span>{getEventEmoji(event?.eventType)}</span>
                        <span>{event?.eventType.toUpperCase()} INVITATION</span>
                    </div>

                    <h1 className="rsvp-event-title">{event?.eventName}</h1>

                    <p className="rsvp-host-greeting">
                        Dear <span className="rsvp-guest-highlight">{recipient?.customFields?.salutation ? `${recipient.customFields.salutation} ` : ''}{recipient?.name}</span>,{' '}
                        {event?.hostName ? `${event.hostName} warmly invites you to celebrate with us!` : 'you are cordially invited to celebrate with us!'}
                    </p>
                </div>

                {/* 2. Media Showcase (Image / Video Preview if attached) */}
                {event?.mediaUrl && (
                    <div className="rsvp-media-preview">
                        {event.mediaType === 'video' ? (
                            <video src={event.mediaUrl} controls poster="" autoPlay={false} />
                        ) : (
                            <img src={event.mediaUrl} alt="Event Invitation Card" />
                        )}
                    </div>
                )}

                {/* 3. Event Details: Date, Time & Venue */}
                {event && (
                    <div className="rsvp-event-meta">
                        <div className="meta-card">
                            <div className="meta-icon-wrap">
                                <Calendar size={19} />
                            </div>
                            <div className="meta-content">
                                <h5>{t.when}</h5>
                                <p>{new Date(event.eventDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                                <span className="meta-time-badge">
                                    <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                    {new Date(event.eventDate).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        </div>

                        <div className="meta-card">
                            <div className="meta-icon-wrap">
                                <MapPin size={19} />
                            </div>
                            <div className="meta-content">
                                <h5>{t.where}</h5>
                                <p>{event.eventVenue}</p>
                                <a href={getGoogleMapsUrl()} target="_blank" rel="noreferrer" className="meta-maps-link">
                                    <span>{event.eventCity} · {t.getDirections}</span>
                                    <ExternalLink size={12} />
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. Event Countdown Timer */}
                {countdown.days > 0 || countdown.hours > 0 ? (
                    <div className="rsvp-countdown-bar">
                        <div className="countdown-unit">
                            <span className="countdown-num">{countdown.days}</span>
                            <span className="countdown-label">{t.days}</span>
                        </div>
                        <div className="countdown-colon">:</div>
                        <div className="countdown-unit">
                            <span className="countdown-num">{countdown.hours}</span>
                            <span className="countdown-label">{t.hours}</span>
                        </div>
                        <div className="countdown-colon">:</div>
                        <div className="countdown-unit">
                            <span className="countdown-num">{countdown.mins}</span>
                            <span className="countdown-label">{t.mins}</span>
                        </div>
                    </div>
                ) : null}

                {/* 5. VIEW / EDIT SWITCH */}
                {isSubmitted && !isEditing ? (
                    /* ─── CONFIRMATION VIEW (Already Responded) ─── */
                    <div className="rsvp-confirmation-view">
                        <div className="confetti-icon">
                            {rsvpStatus === 'accepted' ? '🎉' : (rsvpStatus === 'declined' ? '💌' : '🤔')}
                        </div>

                        <h2 className="confirmation-title">
                            {rsvpStatus === 'accepted' ? 'RSVP Confirmed!' : (rsvpStatus === 'declined' ? 'Response Recorded' : 'Tentative RSVP Noted')}
                        </h2>

                        <p className="confirmation-subtitle">
                            {rsvpStatus === 'accepted' ? t.thankYouAccepted : (rsvpStatus === 'declined' ? t.thankYouDeclined : t.thankYouMaybe)}
                        </p>

                        {/* Response Summary Card */}
                        <div className="confirmation-card">
                            <div className="confirmation-row">
                                <span>Attending Status</span>
                                <span className={`status-tag status-${rsvpStatus}`}>
                                    {rsvpStatus === 'accepted' ? '✓ Attending' : (rsvpStatus === 'declined' ? '✗ Unable to Attend' : '? Tentative')}
                                </span>
                            </div>

                            {rsvpStatus === 'accepted' && (
                                <>
                                    <div className="confirmation-row">
                                        <span>Confirmed Headcount</span>
                                        <span className="confirmation-val">{adultsCount} Adult{adultsCount > 1 ? 's' : ''}{kidsCount > 0 ? `, ${kidsCount} Child${kidsCount > 1 ? 'ren' : ''}` : ''} ({adultsCount + kidsCount} Total)</span>
                                    </div>
                                    {Boolean(event?.collectDietary) && (
                                        <div className="confirmation-row">
                                            <span>Dietary Choice</span>
                                            <span className="confirmation-val">
                                                {dietaryPreference === 'vegetarian' ? '🌱 Vegetarian' :
                                                 dietaryPreference === 'non-vegetarian' ? '🍗 Non-Vegetarian' :
                                                 dietaryPreference === 'jain' ? '🥗 Jain' :
                                                 dietaryPreference === 'vegan' ? '🥑 Vegan' :
                                                 dietaryPreference === 'eggetarian' ? '🥚 Eggetarian' : 'Standard / No Restriction'}
                                            </span>
                                        </div>
                                    )}
                                    {Boolean(event?.collectAccommodation) && (
                                        <>
                                            <div className="confirmation-row">
                                                <span>Accommodation</span>
                                                <span className="confirmation-val">{needsAccommodation ? '🏨 Stay Arrangement Requested' : 'Not Required'}</span>
                                            </div>
                                            {arrivalDate && (
                                                <div className="confirmation-row">
                                                    <span>Arrival</span>
                                                    <span className="confirmation-val">{arrivalDate}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}

                            {rsvpNote && (
                                <div className="confirmation-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                    <span>Your Message to Host</span>
                                    <span style={{ fontStyle: 'italic', fontWeight: 500, color: '#E2E8F0' }}>"{rsvpNote}"</span>
                                </div>
                            )}
                        </div>

                        {/* Calendar & Map Actions */}
                        <div className="confirmation-actions-grid">
                            <a href={getGoogleCalendarUrl()} target="_blank" rel="noreferrer" className="action-btn action-btn-calendar">
                                <Calendar size={15} />
                                <span>{t.addToCalendar}</span>
                            </a>
                            <button type="button" onClick={downloadIcsFile} className="action-btn action-btn-calendar">
                                <Download size={15} />
                                <span>Apple / Outlook</span>
                            </button>
                            <a href={getGoogleMapsUrl()} target="_blank" rel="noreferrer" className="action-btn action-btn-directions">
                                <MapPin size={15} />
                                <span>Venue Map</span>
                            </a>
                        </div>

                        <div style={{ marginTop: '1.75rem' }}>
                            <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="action-btn action-btn-edit"
                                style={{ width: '100%' }}
                            >
                                <Edit3 size={15} />
                                <span>{t.editRsvpBtn}</span>
                            </button>
                        </div>
                    </div>
                ) : (
                    /* ─── RSVP INTERACTIVE FORM ─── */
                    <form onSubmit={handleSubmit} className="rsvp-form-body">
                        <div className="rsvp-section-title">
                            <Sparkles size={18} color="#C084FC" />
                            <span>{t.willYouAttend}</span>
                        </div>

                        {/* 1. Status Selector */}
                        <div className="rsvp-status-selector">
                            <button
                                type="button"
                                className={`status-btn btn-attending ${rsvpStatus === 'accepted' ? 'selected-accepted' : ''}`}
                                onClick={() => setRsvpStatus('accepted')}
                            >
                                <span className="status-emoji">👍</span>
                                <span className="status-title">Attending</span>
                                <span className="status-sub">{t.acceptJoy}</span>
                            </button>

                            <button
                                type="button"
                                className={`status-btn btn-tentative ${rsvpStatus === 'maybe' ? 'selected-maybe' : ''}`}
                                onClick={() => setRsvpStatus('maybe')}
                            >
                                <span className="status-emoji">🤔</span>
                                <span className="status-title">Tentative</span>
                                <span className="status-sub">{t.maybeTentative}</span>
                            </button>

                            <button
                                type="button"
                                className={`status-btn btn-declined ${rsvpStatus === 'declined' ? 'selected-declined' : ''}`}
                                onClick={() => setRsvpStatus('declined')}
                            >
                                <span className="status-emoji">💌</span>
                                <span className="status-title">Decline</span>
                                <span className="status-sub">{t.declineRegret}</span>
                            </button>
                        </div>

                        {/* 2. Attending Specific Fields (Headcount, Diet, Accommodation) */}
                        {rsvpStatus === 'accepted' && (
                            <div className="attending-details-card">
                                {/* Headcount Steppers */}
                                <div className="attending-section-heading">
                                    {t.totalAttendees}
                                </div>

                                <div className="stepper-grid">
                                    <div className="stepper-item">
                                        <div className="stepper-label">
                                            <h6>{t.adults}</h6>
                                        </div>
                                        <div className="stepper-controls">
                                            <button
                                                type="button"
                                                className="stepper-btn"
                                                onClick={() => setAdultsCount(Math.max(1, adultsCount - 1))}
                                                disabled={adultsCount <= 1}
                                            >
                                                -
                                            </button>
                                            <span className="stepper-val">{adultsCount}</span>
                                            <button
                                                type="button"
                                                className="stepper-btn"
                                                onClick={() => setAdultsCount(adultsCount + 1)}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    <div className="stepper-item">
                                        <div className="stepper-label">
                                            <h6>{t.kids}</h6>
                                        </div>
                                        <div className="stepper-controls">
                                            <button
                                                type="button"
                                                className="stepper-btn"
                                                onClick={() => setKidsCount(Math.max(0, kidsCount - 1))}
                                                disabled={kidsCount <= 0}
                                            >
                                                -
                                            </button>
                                            <span className="stepper-val">{kidsCount}</span>
                                            <button
                                                type="button"
                                                className="stepper-btn"
                                                onClick={() => setKidsCount(kidsCount + 1)}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Dietary Preference Pills */}
                                {Boolean(event?.collectDietary) && (
                                    <>
                                        <div className="attending-section-heading" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Utensils size={14} />
                                            <span>{t.dietaryHeading}</span>
                                        </div>

                                        <div className="dietary-pills-row">
                                            {[
                                                { id: 'vegetarian', label: t.dietVeg },
                                                { id: 'non-vegetarian', label: t.dietNonVeg },
                                                { id: 'jain', label: t.dietJain },
                                                { id: 'vegan', label: t.dietVegan },
                                                { id: 'eggetarian', label: t.dietEggetarian },
                                                { id: 'none', label: t.dietNone },
                                            ].map(pill => (
                                                <button
                                                    key={pill.id}
                                                    type="button"
                                                    className={`diet-pill ${dietaryPreference === pill.id ? 'selected' : ''}`}
                                                    onClick={() => setDietaryPreference(pill.id as any)}
                                                >
                                                    {pill.label}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* Accommodation & Travel Requirements */}
                                {Boolean(event?.collectAccommodation) && (
                                    <>
                                        <div
                                            className="accommodation-toggle-row"
                                            onClick={() => setNeedsAccommodation(!needsAccommodation)}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div className="acc-icon-wrap">
                                                    <Home size={18} color="#C084FC" />
                                                </div>
                                                <div>
                                                    <div className="acc-title">{t.accommodationHeading}</div>
                                                    <div className="acc-sub">{t.accommodationDesc}</div>
                                                </div>
                                            </div>
                                            <div className={`toggle-switch ${needsAccommodation ? 'active' : ''}`}>
                                                <div className="toggle-knob" />
                                            </div>
                                        </div>

                                        {needsAccommodation && (
                                            <div className="travel-inputs-grid">
                                                <div>
                                                    <label className="travel-input-label">{t.arrivalDate}</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. 15th Sep, 2:00 PM"
                                                        value={arrivalDate}
                                                        onChange={(e) => setArrivalDate(e.target.value)}
                                                        className="rsvp-input"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="travel-input-label">{t.travelDetails}</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. Flight 6E-204 / By Car"
                                                        value={transportDetails}
                                                        onChange={(e) => setTransportDetails(e.target.value)}
                                                        className="rsvp-input"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* 3. Wishes / Notes for Host */}
                        <div className="rsvp-notes-heading">
                            <MessageSquare size={15} />
                            <span>{t.wishesHeading}</span>
                        </div>

                        <textarea
                            className="rsvp-textarea"
                            placeholder={t.wishesPlaceholder}
                            value={rsvpNote}
                            onChange={(e) => setRsvpNote(e.target.value)}
                        />

                        {/* 4. Submit Button */}
                        <button type="submit" className="rsvp-submit-btn" disabled={submitting}>
                            {submitting ? (
                                <span>{t.submitting}</span>
                            ) : (
                                <>
                                    <Sparkles size={18} />
                                    <span>{isSubmitted ? t.updateRsvp : t.submitRsvp}</span>
                                </>
                            )}
                        </button>

                        {isEditing && (
                            <button
                                type="button"
                                onClick={() => setIsEditing(false)}
                                className="cancel-edit-btn"
                            >
                                Cancel and keep previous response
                            </button>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
};

export default InviteRsvpPage;
